import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import CenterLoading from '../../components/CenterLoading/CenterLoading';
import useDevice from '../../hooks/useDevice';
import useActivateTicket from '../../hooks/useActivateTicket';
import { adjustFontSize, getValueByLanguage, isSameDeviceId } from '../../utils';
import TopContent from './Layout/TopContent';
import MainContent from './Layout/MainContent';
import BottomContent from './Layout/BottomContent';
import DeviceNotification from '../DeviceNotification/DeviceNotification';

import styles from './styles.module.css';
import { OrderTicket, MultilingualType } from 'types';
import { RootState, Theme } from 'redux/reducers/rootReducer';

/**
 * チケット表示
 */
type TicketDisplayProps = {
    orderTicket: OrderTicket;
    currentTheme: Theme;
    activeThemeIndex: number;
};

const TicketDisplay: React.FC<TicketDisplayProps> = React.memo(({ orderTicket, currentTheme, activeThemeIndex }) => {
    const mainLayoutRef = useRef<HTMLDivElement>(null);
    const [circleSize, setCircleSize] = useState(300);
    const { i18n } = useTranslation();
    const { terminateTicket, loading } = useActivateTicket();

    const { deviceId } = useDevice();

    const l = useCallback(
        (text?: MultilingualType | null) => {
            return getValueByLanguage(text, activeThemeIndex === 0 ? i18n.language : 'ja');
        },
        [activeThemeIndex, i18n.language],
    );

    useEffect(() => {
        /**
         * サークルサイズをリセットする関数
         */
        const resetCircleSize = () => {
            if (mainLayoutRef.current) {
                // メインレイアウトの高さと幅からサークルのサイズを計算
                const { width, height } = mainLayoutRef.current.getBoundingClientRect();
                const tempCircleSize = Math.min(600, Math.min(width, height) * 0.8);

                setCircleSize(tempCircleSize);
            }
        };

        let timer: NodeJS.Timeout;
        const delayTrigger = () => {
            clearTimeout(timer);
            timer = setTimeout(resetCircleSize, 100); // 100msだけ画面のサイズ変更が起きない場合に、くるくるをリサイズします。
        };
        resetCircleSize();
        window.addEventListener('resize', delayTrigger);
        window.addEventListener('orientationchange', delayTrigger);
        return () => {
            window.removeEventListener('resize', delayTrigger);
            window.removeEventListener('orientationchange', delayTrigger);
        };
    }, []);

    const { ticketName, ticketNameFontSize } = useMemo(() => {
        const ticketName = l(orderTicket?.ticketNameShort) || l(orderTicket?.ticketName);
        // くるくるの大きさに合わせてフォントサイズを変更する。文面によっては画面からはみ出すこともある。
        const ticketNameFontSize = adjustFontSize(ticketName, circleSize, 30);
        const routeFontSize = Math.min(50, circleSize / 11);
        return { ticketName, ticketNameFontSize, routeFontSize };
    }, [l, orderTicket, circleSize]);

    if (orderTicket == null) {
        return <></>;
    }

    return (
        <div className={styles.wrapper}>
            {loading && <CenterLoading overFooter />}
            <div className={styles.topLayout}>
                <TopContent
                    currentTheme={currentTheme}
                    orderTicket={orderTicket}
                    ticketNameFontSize={ticketNameFontSize}
                    ticketName={ticketName}
                    activeThemeIndex={activeThemeIndex}
                />
            </div>
            <div ref={mainLayoutRef} className={styles.mainLayout}>
                {isSameDeviceId(orderTicket, deviceId) ? (
                    <MainContent
                        orderTicket={orderTicket}
                        currentTheme={currentTheme}
                        circleSize={circleSize}
                        activeThemeIndex={activeThemeIndex}
                    />
                ) : (
                    <DeviceNotification activeThemeIndex={activeThemeIndex} />
                )}
            </div>
            <div className={styles.bottomLayout}>
                <BottomContent
                    orderTicket={orderTicket}
                    currentTheme={currentTheme}
                    circleSize={circleSize}
                    terminateTicket={terminateTicket}
                    isSameDeviceId={isSameDeviceId(orderTicket, deviceId)}
                />
            </div>
        </div>
    );
});

export default connect((state: RootState) => ({
    currentTheme: state.currentTheme ?? {},
    activeThemeIndex: state.activeThemeIndex,
}))(TicketDisplay);
